import type { RefObject } from "react";
import type { VideoJsPlayer } from "./usePlayer";

/**
 * Mutable flag that `usePlayer`'s play/pause event handlers check before
 * updating playing state. Reverse playback sets this to true right before
 * triggering a native pause that it doesn't want reflected in the playing
 * flag (e.g. when transitioning from forward play to reverse play, the
 * native player must pause but the user-visible playing state stays true).
 *
 * Shared by closure between `usePlayer` and `attachReversePlayback`.
 */
export interface SuppressNextPauseRef {
  current: boolean;
}

export interface AttachReversePlaybackOptions {
  player: VideoJsPlayer;
  playerRef: RefObject<VideoJsPlayer | null>;
  playingRef: RefObject<boolean>;
  playbackRateRef: RefObject<number>;
  tempPlaybackRateRef: RefObject<number>;
  initialPlaybackRate: number;
  setPlaying: (playing: boolean) => void;
  setRate: (rate: number, persist: boolean) => void;
  suppressNextPauseRef: SuppressNextPauseRef;
}

/**
 * Bolts reverse-playback support onto a video.js player. HTML5 `<video>`
 * doesn't support negative `playbackRate`, so we fake it: when the rate
 * goes negative, we pause the native player and step `currentTime` backwards
 * via a `setInterval` ticker. The native API is monkey-patched so consumers
 * (hotkeys, controls, settings store) can call `player.play()`,
 * `player.pause()`, `player.playbackRate(-2)` etc. without knowing the
 * direction — and `player.paused()` returns the user-facing meaning, not
 * the native player state.
 *
 * Why a pure function and not a `useReversePlayback` hook:
 *
 * This needs synchronous, closure-shared access to `usePlayer`'s internals
 * (the play/pause event suppression flag, the playing/rate refs, the
 * `setStateOnPause` rate-restore policy). Making it a separate hook would
 * require exposing those internals as a public extension surface
 * (`suppressNextPauseEvent`, `applyPauseStateUpdate`, etc.) — a leaky
 * abstraction that exists only for this one consumer. A function called
 * from inside `usePlayer`'s init effect is the honest shape: tightly
 * coupled to the lifecycle, but isolated to its own file so the "extract
 * or delete" diff stays trivial.
 *
 * Returns a cleanup function that stops the reverse ticker. The
 * monkey-patches don't need to be undone because the player is disposed
 * when the cleanup runs.
 */
export function attachReversePlayback({
  player,
  playerRef,
  playingRef,
  playbackRateRef,
  tempPlaybackRateRef,
  initialPlaybackRate,
  setPlaying,
  setRate,
  suppressNextPauseRef,
}: AttachReversePlaybackOptions): () => void {
  let reverseInterval: ReturnType<typeof setInterval> | null = null;
  let internalRate = initialPlaybackRate;

  const startReverseTicker = () => {
    if (reverseInterval) clearInterval(reverseInterval);
    const fps = 15;
    const dt = 1 / fps;
    let numSkipped = 0;
    reverseInterval = setInterval(() => {
      if (!playerRef.current) return;
      if (playerRef.current.seeking()) {
        numSkipped++;
        return;
      }
      if (playerRef.current.currentTime() > dt) {
        playerRef.current.currentTime(
          Math.max(
            playerRef.current.currentTime() +
              (tempPlaybackRateRef.current / fps) * (numSkipped + 1),
            0,
          ),
        );
      } else {
        playerRef.current.pause();
        playerRef.current.currentTime(0);
      }
      numSkipped = 0;
    }, 1000 / fps);
  };
  const stopReverseTicker = () => {
    if (reverseInterval) {
      clearInterval(reverseInterval);
      reverseInterval = null;
    }
  };

  // Mirrors what usePlayer's pause handler does on a natural pause — kept
  // here so the reverse-stop path explicitly applies the same state.
  const applyPauseStateLocally = () => {
    setPlaying(false);
    playingRef.current = false;
    setRate(playbackRateRef.current, true);
  };
  const applyPlayStateLocally = () => {
    setPlaying(true);
    playingRef.current = true;
  };

  const originalPlay = player.play;
  const originalPause = player.pause;
  const originalPaused = player.paused;
  const originalPlaybackRate = player.playbackRate;

  player.play = playerRef.current!.play = async () => {
    if (internalRate >= 0) {
      try {
        await originalPlay.call(player);
      } catch (err) {
        // Swallow play() rejections (autoplay blocks, NotSupportedError on
        // bad sources). Real issues still surface via the 'error' event.
        console.warn("video play() rejected:", err);
      }
    } else {
      if (!playerRef.current?.paused()) suppressNextPauseRef.current = true;
      originalPause.call(player);
      startReverseTicker();
      applyPlayStateLocally();
    }
  };

  player.pause = playerRef.current!.pause = () => {
    if (internalRate >= 0) {
      const t = player.currentTime();
      originalPause.call(player);
      // Rates > 1x sometimes get the native player out of sync — pin it.
      if (internalRate > 1) player.currentTime(t);
    } else {
      stopReverseTicker();
      applyPauseStateLocally();
    }
    internalRate = playbackRateRef.current;
  };

  player.paused = playerRef.current!.paused = () => {
    if (internalRate >= 0) return originalPaused.call(player);
    return !playingRef.current;
  };

  player.playbackRate = playerRef.current!.playbackRate = ((rate?: number) => {
    if (rate === undefined) return originalPlaybackRate.call(player);
    if (rate >= 0) {
      // Transitioning back to forward: stop the ticker, resume native play.
      const res = originalPlaybackRate.call(player, rate);
      if (internalRate < 0 && playingRef.current) {
        stopReverseTicker();
        originalPlay.call(player).catch(() => {});
      }
      internalRate = rate;
      return res;
    }
    // Transitioning to reverse: pause native, start ticker.
    if (internalRate > 0 && playingRef.current) {
      if (!playerRef.current?.paused()) suppressNextPauseRef.current = true;
      originalPause.call(player);
      startReverseTicker();
    }
    internalRate = rate;
  }) as typeof player.playbackRate;

  return stopReverseTicker;
}
